/**
 * The menu on a page tab (docs/06-UI-SPEC.md §6.5): rename, duplicate, delete.
 *
 * Opened by right-clicking a tab, and closed by choosing something, pressing Escape,
 * or clicking anywhere else. The paper a page is drawn on is changed from the panel
 * on the right rather than from here, so there is one place for it and not two.
 */

import { useEffect } from 'react';

export interface PageTabMenuProps {
  /** Where the tab was right-clicked, in screen pixels. */
  readonly at: { readonly x: number; readonly y: number };
  readonly onRename: () => void;
  readonly onDuplicate: () => void;
  /** Null when this is the only page: a board must always have one. */
  readonly onDelete: (() => void) | null;
  readonly onClose: () => void;
}

interface ItemProps {
  readonly label: string;
  readonly onChoose: (() => void) | null;
}

function Item({ label, onChoose }: ItemProps) {
  return (
    <button
      type="button"
      disabled={onChoose === null}
      onClick={() => onChoose?.()}
      className="flex min-h-touch w-full items-center px-3 text-left text-sm text-text-primary transition-colors duration-fast hover:bg-surface-hover disabled:cursor-not-allowed disabled:text-text-placeholder disabled:hover:bg-transparent"
    >
      {label}
    </button>
  );
}

export function PageTabMenu({ at, onRename, onDuplicate, onDelete, onClose }: PageTabMenuProps) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <>
      {/* Anywhere else closes it, including a right-click somewhere else. */}
      <div
        className="fixed inset-0 z-40"
        onPointerDown={onClose}
        onContextMenu={(event) => {
          event.preventDefault();
          onClose();
        }}
      />
      <div
        role="menu"
        aria-label="Page"
        style={{ left: at.x, top: at.y }}
        className="fixed z-50 w-40 -translate-y-full overflow-hidden rounded-md border border-border bg-surface py-1 shadow-lg"
      >
        <Item label="Rename" onChoose={onRename} />
        <Item label="Duplicate" onChoose={onDuplicate} />
        <Item label="Delete" onChoose={onDelete} />
      </div>
    </>
  );
}
